import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ScrollFadeIn from '../components/ScrollFadeIn';
import { Play, ExternalLink, Boxes, Mic, GraduationCap } from 'lucide-react';

interface Project {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  tags: string[];
  highlights: string[];
  demoUrl?: string;
  projectUrl: string;
  icon: React.ReactNode;
  gradient: string;
}

const projects: Project[] = [
  {
    id: 'aristo',
    name: 'ARISTO',
    subtitle: 'AI-powered Real-time Interactive Speaking Tutor & Orchestrator',
    description:
      'FreakIT가 개발한 AI 기반 구술 튜터링 플랫폼입니다. 학생이 PDF를 업로드하면 AI가 자료를 검색하고, 소크라테스식 질문을 통해 실시간으로 이해도를 평가합니다.',
    tags: ['EdTech', 'AI', 'Voice Tutor', '출처 기반 검색'],
    highlights: [
      'AI 비전·언어 기술 기반 실시간 소크라테스식 구술 튜터링',
      'PDF 교재를 학습해 정확한 출처 기반 질문 생성',
      '안전한 로그인 기반 세션 관리',
      'AI 서버와 백엔드가 분리된 확장형 아키텍처',
    ],
    demoUrl: 'https://youtu.be/zuRYcA1nuX4',
    projectUrl: '/projects/aristo',
    icon: <Mic />,
    gradient: 'linear-gradient(135deg, #1e293b 0%, #020617 100%)',
  },
  {
    id: 'kaistudy',
    name: 'KAIStudy',
    subtitle: 'KAIST 학생 그룹 과외 플랫폼',
    description:
      'KAIST 재학생들이 직접 진행하는 프리미엄 그룹 과외 서비스입니다. 검증된 강사진과 소규모 그룹, 맞춤형 커리큘럼으로 효율적인 학습 경험을 제공합니다.',
    tags: ['EdTech', '그룹 과외', 'KAIST'],
    highlights: [
      'KAIST 재학생 검증 강사진',
      '소규모 그룹 맞춤형 수업',
      '체계적인 커리큘럼 설계',
      '학습 목표 기반 진도 관리',
    ],
    projectUrl: '/kaistudy',
    icon: <GraduationCap />,
    gradient: 'linear-gradient(135deg, #020617 0%, #1e293b 100%)',
  },
  {
    id: 'vta',
    name: 'VTA Value Type Test',
    subtitle: '가치관 유형 검사 (Value Type Assessment)',
    description:
      'React 기반 가치관 유형 검사 애플리케이션입니다. 사용자의 답변을 분석하여 개인의 가치관 유형을 진단하고 결과를 시각적으로 제공합니다.',
    tags: ['Web App', 'Assessment', 'React'],
    highlights: [
      '가치관 유형을 진단하는 설문 기반 검사',
      'React 기반 단일 페이지 애플리케이션',
      '직관적인 UI/UX로 누구나 쉽게 이용 가능',
    ],
    demoUrl: 'https://vta.freakit.co.kr/',
    projectUrl: '/projects/vta',
    icon: <Boxes />,
    gradient: 'linear-gradient(135deg, #020617 0%, #0f172a 100%)',
  },
];

const ProjectsPage: React.FC = () => {
  return (
    <PageContainer>
      <TechProjectSection>
        <HeroContent>
          <HeroTitle>사이드 프로젝트</HeroTitle>
          <HeroDescription>
            FreakIT가 만들어온 혁신적인 사이드 프로젝트들을 소개합니다.
            AI 튜터링부터 전략 게임, 심리 검사까지 다양한 분야에서 기술의 가능성을 탐구합니다.
          </HeroDescription>
        </HeroContent>
      </TechProjectSection>

      <FeaturedSection>
        <ProjectsList>
          {projects.map((project, index) => (
            <ScrollFadeIn key={project.id} direction="up" delay={0.1 * (index + 1)}>
              <ProjectCard to={project.projectUrl}>
                <ProjectCardHeader gradient={project.gradient}>
                  <ProjectIcon>{project.icon}</ProjectIcon>
                  <ProjectHeaderText>
                    <ProjectName>{project.name}</ProjectName>
                    <ProjectSubtitle>{project.subtitle}</ProjectSubtitle>
                  </ProjectHeaderText>
                </ProjectCardHeader>

                <ProjectCardBody>
                  <TagRow>
                    {project.tags.map((tag) => (
                      <Tag key={tag}>{tag}</Tag>
                    ))}
                  </TagRow>

                  <ProjectDescription>{project.description}</ProjectDescription>

                  <HighlightsTitle>주요 특징</HighlightsTitle>
                  <HighlightsList>
                    {project.highlights.map((h, i) => (
                      <HighlightItem key={i}>{h}</HighlightItem>
                    ))}
                  </HighlightsList>

                  <CardButtons>
                    <ProjectLinkText>
                      자세히 보기 →
                    </ProjectLinkText>
                    {project.demoUrl && (
                      <DemoButton
                        href={project.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {project.id === 'aristo' ? (
                          <><Play /> 시연 영상</>
                        ) : (
                          <><ExternalLink /> 바로가기</>
                        )}
                      </DemoButton>
                    )}
                  </CardButtons>
                </ProjectCardBody>
              </ProjectCard>
            </ScrollFadeIn>
          ))}
        </ProjectsList>
      </FeaturedSection>
    </PageContainer>
  );
};

export default ProjectsPage;

/* ────────── Styled Components ────────── */

const PageContainer = styled.div`
  padding-top: 80px;
`;

const TechProjectSection = styled.section`
  padding: 5rem 2rem;
  background: transparent;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h2`
  font-size: 2.4rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1.5rem;
`;

const HeroDescription = styled.p`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.8;
`;

const FeaturedSection = styled.section`
  padding: 4rem 2rem;
  background: transparent;
`;

const ProjectsList = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const ProjectCard = styled(Link)`
  background: ${({ theme }) => theme.colors.bgSecondary};
  backdrop-filter: blur(12px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  transition: ${({ theme }) => theme.transitions.cubic};
  display: flex;
  flex-direction: column;
  text-decoration: none;

  &:hover {
    transform: translateY(-12px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(15, 23, 42, 0.4);
  }
`;

const ProjectCardHeader = styled.div<{ gradient: string }>`
  padding: 2rem;
  background: ${({ gradient }) => gradient};
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
`;

const ProjectIcon = styled.div`
  font-size: 2.4rem;
  color: white;
`;

const ProjectHeaderText = styled.div``;

const ProjectName = styled.h3`
  font-size: 1.7rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.5rem;
`;

const ProjectSubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
`;

const ProjectCardBody = styled.div`
  padding: 2rem;
`;

const TagRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const Tag = styled.span`
  padding: 0.4rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 100px;
  font-size: 0.85rem;
  color: white;
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin-bottom: 2.5rem;
`;

const HighlightsTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1.2rem;
`;

const HighlightsList = styled.ul`
  margin: 0 0 2.5rem 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const HighlightItem = styled.li`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding-left: 1.5rem;
  position: relative;
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
  }
`;

const CardButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ProjectLinkText = styled.span`
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
`;

const DemoButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: white;
  color: #020617;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    transform: translateY(-2px);
  }

  svg {
    font-size: 1rem;
  }
`;


